<script setup lang="ts">
  import { SidebarMenuButton } from '@gat-ship/vue-components'
  import { accessCodes } from '@gat-ship/types'
  import {
    IconChevronDown,
    IconChevronRight,
    IconCurrency,
    IconAccessControl,
    IconCustomFields,
    IconNetwork,
    IconPortcall,
    IconSliders,
    IconTenantUsers
  } from '@gat-ship/icons'
  import { useUser } from '@gat-ship/user'
  import { computed, inject, ref, watch } from 'vue'
  import {
    ADMIN_TARGETS,
    adminMenuNavKey,
    type AdminMenuNav,
    type AdminNavTarget
  } from './admin-menu-nav'

  const user = useUser()

  // The menu now lives only inside `AdministrationModal`, which provides the
  // active-key-backed panel-switcher strategy. (The route-driven section was
  // retired in central-modal-host Phase 7.)
  const injectedNav = inject(adminMenuNavKey)
  if (!injectedNav) {
    throw new Error(
      'AdministrationMainMenu must be rendered within AdministrationModal'
    )
  }
  const nav: AdminMenuNav = injectedNav

  /**
   * Auto-open for a collapsible group: opens whenever one of the group's
   * targets is active (path-prefix match in the route host, active-key
   * match in the modal), while leaving manual toggling free.
   */
  function useGroupOpen(targets: readonly AdminNavTarget[]) {
    const open = ref(false)
    const isChildActive = computed(() => nav.isGroupActive(targets))
    watch(
      isChildActive,
      (value) => {
        if (value) {
          open.value = true
        }
      },
      { immediate: true }
    )
    return { open, isChildActive }
  }

  const { open: accessControlOpen, isChildActive: accessControlActive } =
    useGroupOpen([ADMIN_TARGETS.accessRoles])
  const { open: organizationOpen, isChildActive: organizationActive } =
    useGroupOpen([ADMIN_TARGETS.agencyStructure, ADMIN_TARGETS.companies])
  const { open: monetaryOpen, isChildActive: monetaryActive } = useGroupOpen([
    ADMIN_TARGETS.currencies
  ])
  const { open: portcallOpen, isChildActive: portcallActive } = useGroupOpen([
    ADMIN_TARGETS.estimatedTimeNames,
    ADMIN_TARGETS.portcallTypes
  ])
  const { open: settingsOpen, isChildActive: settingsActive } = useGroupOpen([
    ADMIN_TARGETS.agencySettings
  ])
</script>

<template>
  <div
    v-if="user.isTenantAdmin.value"
    class="text-muted-foreground text-xs uppercase"
  >
    Tenant
  </div>
  <SidebarMenuButton
    v-if="user.isTenantAdmin.value"
    :as="nav.hrefFor(ADMIN_TARGETS.tenantUsers) ? 'a' : 'button'"
    :href="nav.hrefFor(ADMIN_TARGETS.tenantUsers) ?? undefined"
    variant="nav"
    :is-active="nav.isItemActive(ADMIN_TARGETS.tenantUsers)"
    :data-exact-active="nav.isItemExactActive(ADMIN_TARGETS.tenantUsers)"
    @click="nav.activate($event, ADMIN_TARGETS.tenantUsers)"
    @auxclick="nav.activate($event, ADMIN_TARGETS.tenantUsers)"
  >
    <icon-tenant-users class="lucide-icon shrink-0" />
    <div class="flex grow items-center gap-2">
      <div class="flex-1 text-sm">Tenant Users</div>
      <icon-chevron-right
        v-if="nav.isItemActive(ADMIN_TARGETS.tenantUsers)"
        class="lucide-icon size-4 shrink-0"
      />
    </div>
  </SidebarMenuButton>
  <div
    v-if="user.isTenantAdmin.value"
    class="text-muted-foreground pt-4 text-xs uppercase"
  >
    {{ user.databases.value.current?.name }}
  </div>
  <div v-if="user.isTenantAdmin.value" class="flex w-full flex-col">
    <SidebarMenuButton
      variant="nav"
      @click="accessControlOpen = !accessControlOpen"
    >
      <icon-access-control
        class="lucide-icon shrink-0"
        :class="{ 'text-primary': accessControlActive }"
      />
      <div class="flex grow items-center gap-2">
        <div class="flex-1 text-sm">Access Control</div>
        <icon-chevron-down
          class="lucide-icon transition-all-default text-muted-foreground size-4 shrink-0 duration-200 ease-out"
          :class="{ 'rotate-180': accessControlOpen }"
        />
      </div>
    </SidebarMenuButton>
    <div
      class="relative flex w-full flex-col gap-0 transition-all duration-200 ease-out"
      :class="{ 'overflow-y-hidden': !accessControlOpen }"
      :style="{ maxHeight: accessControlOpen ? '1000px' : '0px' }"
    >
      <div
        class="bg-border left-4.5 absolute bottom-0 top-0 z-50 my-2 w-[2px] rounded-lg"
      ></div>
      <SidebarMenuButton
        :as="nav.hrefFor(ADMIN_TARGETS.accessRoles) ? 'a' : 'button'"
        :href="nav.hrefFor(ADMIN_TARGETS.accessRoles) ?? undefined"
        variant="nav"
        class="ml-6 w-auto before:-left-11"
        :is-active="nav.isItemActive(ADMIN_TARGETS.accessRoles)"
        :data-exact-active="nav.isItemExactActive(ADMIN_TARGETS.accessRoles)"
        @click="nav.activate($event, ADMIN_TARGETS.accessRoles)"
        @auxclick="nav.activate($event, ADMIN_TARGETS.accessRoles)"
      >
        <div class="flex grow items-center gap-2">
          <div class="flex-1 text-sm">Access Roles</div>
          <icon-chevron-right
            v-if="nav.isItemActive(ADMIN_TARGETS.accessRoles)"
            class="lucide-icon size-4 shrink-0"
          />
        </div>
      </SidebarMenuButton>
    </div>
  </div>
  <div
    v-if="
      user.isTenantAdmin.value ||
      user.hasPermission(accessCodes.system.agency.viewAgencyStructure)
    "
    class="flex w-full flex-col"
  >
    <SidebarMenuButton
      variant="nav"
      @click="organizationOpen = !organizationOpen"
    >
      <icon-network
        class="lucide-icon shrink-0"
        :class="{ 'text-primary': organizationActive }"
      />
      <div class="flex grow items-center gap-2">
        <div class="flex-1 text-sm">Organization</div>
        <icon-chevron-down
          class="lucide-icon transition-all-default text-muted-foreground size-4 shrink-0 duration-200 ease-out"
          :class="{ 'rotate-180': organizationOpen }"
        />
      </div>
    </SidebarMenuButton>
    <div
      class="relative flex w-full flex-col gap-0 transition-all duration-200 ease-out"
      :class="{ 'overflow-y-hidden': !organizationOpen }"
      :style="{ maxHeight: organizationOpen ? '1000px' : '0px' }"
    >
      <div
        class="bg-border left-4.5 absolute bottom-0 top-0 z-50 my-2 w-[2px] rounded-lg"
      ></div>
      <SidebarMenuButton
        v-if="user.hasPermission(accessCodes.system.agency.viewAgencyStructure)"
        :as="nav.hrefFor(ADMIN_TARGETS.agencyStructure) ? 'a' : 'button'"
        :href="nav.hrefFor(ADMIN_TARGETS.agencyStructure) ?? undefined"
        variant="nav"
        class="ml-6 w-auto before:-left-11"
        :is-active="nav.isItemActive(ADMIN_TARGETS.agencyStructure)"
        :data-exact-active="
          nav.isItemExactActive(ADMIN_TARGETS.agencyStructure)
        "
        @click="nav.activate($event, ADMIN_TARGETS.agencyStructure)"
        @auxclick="nav.activate($event, ADMIN_TARGETS.agencyStructure)"
      >
        <div class="flex grow items-center gap-2">
          <div class="flex-1 text-sm">Agency Structure</div>
          <icon-chevron-right
            v-if="nav.isItemActive(ADMIN_TARGETS.agencyStructure)"
            class="lucide-icon size-4 shrink-0"
          />
        </div>
      </SidebarMenuButton>
      <SidebarMenuButton
        v-if="user.isTenantAdmin.value"
        :as="nav.hrefFor(ADMIN_TARGETS.companies) ? 'a' : 'button'"
        :href="nav.hrefFor(ADMIN_TARGETS.companies) ?? undefined"
        variant="nav"
        class="ml-6 w-auto before:-left-11"
        :is-active="nav.isItemActive(ADMIN_TARGETS.companies)"
        :data-exact-active="nav.isItemExactActive(ADMIN_TARGETS.companies)"
        @click="nav.activate($event, ADMIN_TARGETS.companies)"
        @auxclick="nav.activate($event, ADMIN_TARGETS.companies)"
      >
        <div class="flex grow items-center gap-2">
          <div class="flex-1 text-sm">Companies</div>
          <icon-chevron-right
            v-if="nav.isItemActive(ADMIN_TARGETS.companies)"
            class="lucide-icon size-4 shrink-0"
          />
        </div>
      </SidebarMenuButton>
    </div>
  </div>
  <div
    v-if="user.hasPermission(accessCodes.system.currency.manageCurrencies)"
    class="flex w-full flex-col"
  >
    <SidebarMenuButton variant="nav" @click="monetaryOpen = !monetaryOpen">
      <icon-currency
        class="lucide-icon shrink-0"
        :class="{ 'text-primary': monetaryActive }"
      />
      <div class="flex grow items-center gap-2">
        <div class="flex-1 text-sm">Monetary</div>
        <icon-chevron-down
          class="lucide-icon transition-all-default text-muted-foreground size-4 shrink-0 duration-200 ease-out"
          :class="{ 'rotate-180': monetaryOpen }"
        />
      </div>
    </SidebarMenuButton>
    <div
      class="relative flex w-full flex-col gap-0 transition-all duration-200 ease-out"
      :class="{ 'overflow-y-hidden': !monetaryOpen }"
      :style="{ maxHeight: monetaryOpen ? '1000px' : '0px' }"
    >
      <div
        class="bg-border left-4.5 absolute bottom-0 top-0 z-50 my-2 w-[2px] rounded-lg"
      ></div>
      <SidebarMenuButton
        :as="nav.hrefFor(ADMIN_TARGETS.currencies) ? 'a' : 'button'"
        :href="nav.hrefFor(ADMIN_TARGETS.currencies) ?? undefined"
        variant="nav"
        class="ml-6 w-auto before:-left-11"
        :is-active="nav.isItemActive(ADMIN_TARGETS.currencies)"
        :data-exact-active="nav.isItemExactActive(ADMIN_TARGETS.currencies)"
        @click="nav.activate($event, ADMIN_TARGETS.currencies)"
        @auxclick="nav.activate($event, ADMIN_TARGETS.currencies)"
      >
        <div class="flex grow items-center gap-2">
          <div class="flex-1 text-sm">Currencies</div>
          <icon-chevron-right
            v-if="nav.isItemActive(ADMIN_TARGETS.currencies)"
            class="lucide-icon size-4 shrink-0"
          />
        </div>
      </SidebarMenuButton>
    </div>
  </div>
  <SidebarMenuButton
    v-if="user.isTenantAdmin.value"
    :as="nav.hrefFor(ADMIN_TARGETS.customFields) ? 'a' : 'button'"
    :href="nav.hrefFor(ADMIN_TARGETS.customFields) ?? undefined"
    variant="nav"
    :is-active="nav.isItemActive(ADMIN_TARGETS.customFields)"
    :data-exact-active="nav.isItemExactActive(ADMIN_TARGETS.customFields)"
    @click="nav.activate($event, ADMIN_TARGETS.customFields)"
    @auxclick="nav.activate($event, ADMIN_TARGETS.customFields)"
  >
    <icon-custom-fields class="lucide-icon shrink-0" />
    <div class="flex grow items-center gap-2">
      <div class="flex-1 text-sm">Field Customization</div>
      <icon-chevron-right
        v-if="nav.isItemActive(ADMIN_TARGETS.customFields)"
        class="lucide-icon size-4 shrink-0"
      />
    </div>
  </SidebarMenuButton>
  <div v-if="user.isTenantAdmin.value" class="flex w-full flex-col">
    <SidebarMenuButton variant="nav" @click="portcallOpen = !portcallOpen">
      <icon-portcall
        class="lucide-icon shrink-0"
        :class="{ 'text-primary': portcallActive }"
      />
      <div class="flex grow items-center gap-2">
        <div class="flex-1 text-sm">Portcall</div>
        <icon-chevron-down
          class="lucide-icon transition-all-default text-muted-foreground size-4 shrink-0 duration-200 ease-out"
          :class="{ 'rotate-180': portcallOpen }"
        />
      </div>
    </SidebarMenuButton>
    <div
      class="relative flex w-full flex-col gap-0 transition-all duration-200 ease-out"
      :class="{ 'overflow-y-hidden': !portcallOpen }"
      :style="{ maxHeight: portcallOpen ? '1000px' : '0px' }"
    >
      <div
        class="bg-border left-4.5 absolute bottom-0 top-0 z-50 my-2 w-[2px] rounded-lg"
      ></div>
      <SidebarMenuButton
        :as="nav.hrefFor(ADMIN_TARGETS.estimatedTimeNames) ? 'a' : 'button'"
        :href="nav.hrefFor(ADMIN_TARGETS.estimatedTimeNames) ?? undefined"
        variant="nav"
        class="ml-6 w-auto before:-left-11"
        :is-active="nav.isItemActive(ADMIN_TARGETS.estimatedTimeNames)"
        :data-exact-active="
          nav.isItemExactActive(ADMIN_TARGETS.estimatedTimeNames)
        "
        @click="nav.activate($event, ADMIN_TARGETS.estimatedTimeNames)"
        @auxclick="nav.activate($event, ADMIN_TARGETS.estimatedTimeNames)"
      >
        <div class="flex grow items-center gap-2">
          <div class="flex-1 text-sm">Estimated Times</div>
          <icon-chevron-right
            v-if="nav.isItemActive(ADMIN_TARGETS.estimatedTimeNames)"
            class="lucide-icon size-4 shrink-0"
          />
        </div>
      </SidebarMenuButton>
      <SidebarMenuButton
        :as="nav.hrefFor(ADMIN_TARGETS.portcallTypes) ? 'a' : 'button'"
        :href="nav.hrefFor(ADMIN_TARGETS.portcallTypes) ?? undefined"
        variant="nav"
        class="ml-6 w-auto before:-left-11"
        :is-active="nav.isItemActive(ADMIN_TARGETS.portcallTypes)"
        :data-exact-active="nav.isItemExactActive(ADMIN_TARGETS.portcallTypes)"
        @click="nav.activate($event, ADMIN_TARGETS.portcallTypes)"
        @auxclick="nav.activate($event, ADMIN_TARGETS.portcallTypes)"
      >
        <div class="flex grow items-center gap-2">
          <div class="flex-1 text-sm">Portcall Type</div>
          <icon-chevron-right
            v-if="nav.isItemActive(ADMIN_TARGETS.portcallTypes)"
            class="lucide-icon size-4 shrink-0"
          />
        </div>
      </SidebarMenuButton>
    </div>
  </div>
  <div class="flex w-full flex-col">
    <SidebarMenuButton variant="nav" @click="settingsOpen = !settingsOpen">
      <icon-sliders
        class="lucide-icon shrink-0"
        :class="{ 'text-primary': settingsActive }"
      />
      <div class="flex grow items-center gap-2">
        <div class="flex-1 text-sm">Settings</div>
        <icon-chevron-down
          class="lucide-icon transition-all-default text-muted-foreground size-4 shrink-0 duration-200 ease-out"
          :class="{ 'rotate-180': settingsOpen }"
        />
      </div>
    </SidebarMenuButton>
    <div
      class="relative flex w-full flex-col gap-0 transition-all duration-200 ease-out"
      :class="{ 'overflow-y-hidden': !settingsOpen }"
      :style="{ maxHeight: settingsOpen ? '1000px' : '0px' }"
    >
      <div
        class="bg-border left-4.5 absolute bottom-0 top-0 z-50 my-2 w-[2px] rounded-lg"
      ></div>
      <SidebarMenuButton
        :as="nav.hrefFor(ADMIN_TARGETS.agencySettings) ? 'a' : 'button'"
        :href="nav.hrefFor(ADMIN_TARGETS.agencySettings) ?? undefined"
        variant="nav"
        class="ml-6 w-auto before:-left-11"
        :is-active="nav.isItemActive(ADMIN_TARGETS.agencySettings)"
        :data-exact-active="nav.isItemExactActive(ADMIN_TARGETS.agencySettings)"
        @click="nav.activate($event, ADMIN_TARGETS.agencySettings)"
        @auxclick="nav.activate($event, ADMIN_TARGETS.agencySettings)"
      >
        <div class="flex grow items-center gap-2">
          <div class="flex-1 text-sm">Agency Settings</div>
          <icon-chevron-right
            v-if="nav.isItemActive(ADMIN_TARGETS.agencySettings)"
            class="lucide-icon size-4 shrink-0"
          />
        </div>
      </SidebarMenuButton>
    </div>
  </div>
</template>
